import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getProducts, getFlavors } from '../services/api';
import { products as mockProducts, flavors as mockFlavors } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Check } from 'lucide-react';

const ShopPage = () => {
  const [products, setProducts] = useState(mockProducts);
  const [flavors, setFlavors] = useState(mockFlavors);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getProducts(), getFlavors()])
      .then(([p, f]) => { setProducts(p); setFlavors(f); })
      .catch(() => { setProducts(mockProducts); setFlavors(mockFlavors); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="min-h-screen pt-20" data-testid="shop-page">
      <SEO
        title="Cumpără Toate Aromele"
        url="https://seltz.eu/shop"
        description="Descoperă toate aromele Seltz: Piersică, Lămâie, Cireșe, Grapefruit. Comandă acum băutura ta preferată."
      />
      {/* Hero */}
      <section
        className="py-20 text-center"
        style={{ background: 'linear-gradient(145deg, #0f0f0f 0%, #0a0a0a 100%)' }}
      >
        <div className="flex justify-center -mb-8 lg:-mb-16">
          <img
            src="/assets/images/logo.png"
            alt="SELTZ"
            className="h-48 md:h-64 lg:h-80 w-auto object-contain invert brightness-0 drop-shadow-lg"
          />
        </div>
        <p className="text-white/80 text-lg max-w-md mx-auto relative z-10" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Găsește-ți aroma preferată. Seltz te invită la distracție.
        </p>
      </section>

      {/* All Flavors */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2
            className="text-[#0a0a0a] font-black italic text-2xl mb-10 text-center"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            toate aromele
          </h2>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0a0a0a] border-t-transparent" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {products.map((product) => (
                <ShopProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

const ShopProductCard = ({ product }) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4 group">
      <Link to={`/products/${product.id}`} className="block">
        <div
          className="relative rounded-3xl overflow-hidden flex items-center justify-center transition-all duration-300 group-hover:shadow-2xl"
          style={{
            background: 'linear-gradient(160deg, #1a1a1a 0%, #0a0a0a 50%, #111111 100%)',
            minHeight: '360px',
            padding: '3rem 2rem',
          }}
        >
          {product.badge && (
            <span
              className="absolute top-4 left-4 bg-white text-[#0a0a0a] font-black text-xs px-3 py-1 rounded-full"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              {product.badge}
            </span>
          )}
          <img
            src={product.image}
            alt={product.name}
            className="w-full max-w-[180px] h-auto object-contain transition-transform duration-500 group-hover:scale-110 group-hover:-translate-y-2"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </Link>

      <div className="text-center">
        <h3
          className="text-[#0a0a0a] font-black italic text-xl mb-1"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          {product.name}
        </h3>
        <p className="text-gray-500 text-sm mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {product.cans} doze • ${product.price}
        </p>
        <p className="text-gray-500 text-xs mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
          {product.specs}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <Link
          to={`/products/${product.id}`}
          className="block w-full text-center text-[#0a0a0a] font-black italic text-sm py-3 rounded-2xl border-2 border-[#0a0a0a] hover:bg-gray-50 transition-colors"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          despre produs
        </Link>
        <button
          data-testid={`buy-now-${product.id}`}
          className={`w-full flex items-center justify-center gap-2 font-black italic text-sm py-3 rounded-2xl transition-all duration-200 hover:scale-105 shadow-md ${added ? 'bg-green-500 text-white' : 'bg-[#0a0a0a] text-white hover:bg-[#222222]'
            }`}
          style={{ fontFamily: 'Nunito, sans-serif' }}
          onClick={handleAdd}
        >
          {added ? <><Check size={14} /> adăugat!</> : <><ShoppingCart size={14} /> cumpără acum</>}
        </button>
      </div>
    </div>
  );
};

export default ShopPage;
